import classNames from 'classnames'
import { useCallback, useMemo } from 'react'

import type { ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'

import ActionMenu from '~/components/ActionMenu'
import { VanillaBadge as Badge } from '~/components/Badges'
import Tooltip from '~/components/Tooltip'
import { copyText } from '~/utils/navigator'

import './styles.scss'

export interface Props {
  categories: string[]
  coloringTemplateId: string
  disabled?: boolean
  mediaUrl: string
  onOpenAddToCategoryModal: VoidFunction
  onOpenAddToDailyModal: VoidFunction
  prompt?: string
  templateOrigin: string
  zoomOnClick?: (mediaUrl: string) => void
}

const CatalogMediaCard = ({
  categories,
  coloringTemplateId,
  disabled,
  mediaUrl,
  onOpenAddToCategoryModal,
  onOpenAddToDailyModal,
  prompt,
  templateOrigin,
  zoomOnClick,
}: Props): ReactElement => {
  /** Local state */

  const computedDownloadUrl = `${mediaUrl}?'height=130&width=130'&fit=cover`

  const componentClasses = classNames('catalog-media-card-component__grid', templateOrigin?.toLocaleLowerCase(), {
    'catalog-media-card-component__grid--disabled': disabled,
    'catalog-media-card-component__grid--with-zoom': !!zoomOnClick,
  })

  /** Handlers */

  const handleDrawingClick = () => zoomOnClick && zoomOnClick(mediaUrl)

  const handleCopyId = useCallback(() => copyText(coloringTemplateId, 'Copied to clipboard!'), [coloringTemplateId])

  /** Render */

  const menu: Menu = useMemo(
    () => ({
      actions: [
        {
          action: () => onOpenAddToCategoryModal(),
          label: 'Add to category',
        },
        {
          action: () => onOpenAddToDailyModal(),
          label: 'Set as daily drawing',
        },
        {
          action: () => handleCopyId(),
          className: 'catalog-media-card-component__action-menu__menu-item__copy-id',
          label: 'Copy ID',
        },
      ],
    }),
    [handleCopyId, onOpenAddToCategoryModal, onOpenAddToDailyModal]
  )

  /** Render */

  return (
    <div className={componentClasses}>
      <div className="catalog-media-card-component__grid__header">
        <div
          className={classNames('catalog-media-card-component__grid__header__info', {
            'catalog-media-card-component__grid__header__info--no-categories': !categories || categories.length === 0,
          })}
        >
          <span className="catalog-media-card-component__grid__header__info__text" title={prompt || coloringTemplateId}>
            {prompt || coloringTemplateId}
          </span>
          <ActionMenu disabled={disabled} menu={menu} />
        </div>
        {categories?.length > 0 ? (
          <div className="catalog-media-card-component__grid__header__categories">
            {categories
              ?.slice(0, 2)
              .map((cat, index) => <Badge className="categories-badge" key={cat + index} value={cat} />)}
            {categories?.length > 2 ? (
              <Tooltip
                align={{ points: ['br', 'tr'] }}
                overlay={
                  <div className="catalog-categories-tooltip-content">
                    {categories?.slice(2).map((cat) => <Badge key={coloringTemplateId} outlined value={cat} />)}
                  </div>
                }
                placement="top"
                trigger="hover"
              >
                <div className="counter">+{categories?.length - 2}</div>
              </Tooltip>
            ) : null}
          </div>
        ) : (
          <div className="catalog-media-card-component__grid__header__empty-categories" />
        )}
      </div>
      <div className="catalog-media-card-component__grid__media" onClick={handleDrawingClick}>
        <img className="catalog-media-card-component__grid__media__image" src={computedDownloadUrl} />
      </div>
    </div>
  )
}

export default CatalogMediaCard
